import { formatDistance } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import defaultSkinTone, { SkinTone } from '@cartwhl/3d/gltf/defaultSkinTone';
import { type Character } from '@cartwhl/common/api/getCharacters';
import { HAND_OPTIONS } from '@cartwhl/common/types';
import type { AsyncState } from '@cartwhl/common/types';
import { getResourcesUrl } from '@cartwhl/common/utils';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Image from '@cartwhl/ui/atoms/Image';
import ToggleGroup from '@cartwhl/ui/atoms/ToggleGroup';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';
import ColorPicker from '@cartwhl/ui/molecules/ColorPicker';
import HuePicker from '@cartwhl/ui/molecules/HuePicker';
import SubPanelButton from '@cartwhl/ui/molecules/SubPanelButton';

import { noticesStore } from '../../../../stores/global';
import type { CharacterData, MotionEditorCharacterSettings } from '../../types';

type CharacterTab = 'custom' | 'free';

const DEFAULT_TAB: CharacterTab = 'free';
const CUSTOM_TAB: CharacterTab = 'custom';
const CHARACTER_PICKER_SUBPANEL_KEY = 'character-picker';
const HAND_PICKER_SUBPANEL_KEY = 'hand-picker';
const FACE_PICKER_SUBPANEL_KEY = 'face-picker';
const HAND_OPTIONS_LIST = [
  HAND_OPTIONS.RELAXED,
  HAND_OPTIONS.CURLED,
  HAND_OPTIONS.OPEN_LOOSE,
  HAND_OPTIONS.OPEN_TIGHT,
  HAND_OPTIONS.FIST,
  HAND_OPTIONS.FIST_CLENCHED,
  HAND_OPTIONS.FIST_RELAXED,
  HAND_OPTIONS.POINTING,
  HAND_OPTIONS.POINTING_COOL,
  HAND_OPTIONS.THUMBS_UP,
  HAND_OPTIONS.PEACE,
  HAND_OPTIONS.ROCK,
  HAND_OPTIONS.LOVE_YOU,
  HAND_OPTIONS.GRIPPING,
  HAND_OPTIONS.SPLAY,
  HAND_OPTIONS.CLAW,
] as const;
const FACE_EXPRESSIONS_LIST = [
  'Default',
  'Angry',
  'Annoyed',
  'Bored',
  'Confusion',
  'DelightedGrin',
  'Delighted',
  'Excited',
  'Goof',
  'HappyLookLeft',
  'HappyLookRight',
  'Love',
  'Nervous',
  'Neutral',
  'Pain',
  'Pleased',
  'Puzzled',
  'Rage',
  'RollLookLeft',
  'RollLookRight',
  'Sad',
  'SadEyesClosed',
  'SadLookLeft',
  'SadLookRight',
  'Squeeze',
  'Stretch',
  'Stunned',
  'Surprise',
  'Suspicious',
  'ThinkingLookLeft',
  'ThinkingLookRight',
  'Wink',
] as const;

interface CharacterPanelProps {
  availableCharacters: AsyncState<Character[]>;
  selectedReferenceName: string | null;
  selectedCharacter: CharacterData | null;
  characterSettings: MotionEditorCharacterSettings;
  isMhrCharacter: boolean;
  onSelectCharacter: (
    characterID: string,
    referenceName: string | null,
  ) => Promise<boolean | void> | boolean | void;
  onCharacterSettingsChange: (
    nextSettings: Partial<MotionEditorCharacterSettings>,
    referenceName: string | null,
  ) => void;
}

export default function CharacterPanel({
  availableCharacters,
  selectedReferenceName,
  selectedCharacter,
  characterSettings,
  isMhrCharacter,
  onSelectCharacter,
  onCharacterSettingsChange,
}: CharacterPanelProps) {
  const { charUploadID, skinHex, faceExpression, staticHands } =
    characterSettings;
  const baseURL = getResourcesUrl(false);
  const sceneCharacterID = selectedCharacter?.characterID ?? charUploadID;
  const selectedCharacterMeta = useMemo(
    () => findCharacterMeta(availableCharacters.data, sceneCharacterID),
    [availableCharacters.data, sceneCharacterID],
  );
  const characterDisplayName =
    selectedCharacterMeta?.characterName ??
    formatSceneCharacterLabel(sceneCharacterID);
  const pickerButtonRef = useRef<HTMLButtonElement | null>(null);
  const handButtonRef = useRef<HTMLButtonElement | null>(null);
  const faceButtonRef = useRef<HTMLButtonElement | null>(null);
  const freeCharacters = useMemo(() => {
    if (!availableCharacters.data) {
      return [];
    }

    const prioritized: Character[] = [];
    const rest: Character[] = [];
    const remaining = availableCharacters.data.filter(isFreeCharacter);
    const priorityIDs = [
      'char-upload-Axel',
      'char-upload-Mani',
      'char-upload-Q',
      'char-upload-UEFN-Mannequin',
    ];

    for (const characterID of priorityIDs) {
      const nextIndex = remaining.findIndex(
        (character) => character.PK === characterID,
      );
      if (nextIndex !== -1) {
        prioritized.push(remaining.splice(nextIndex, 1)[0]);
      }
    }

    rest.push(...remaining.sort(byName));
    return [...prioritized, ...rest];
  }, [availableCharacters.data]);
  const customCharacters = useMemo(() => {
    if (!availableCharacters.data) {
      return [];
    }

    return availableCharacters.data
      .filter(isCustomCharacter)
      .filter((character) => character.uploadStatus !== 'FAILED')
      .sort(byLatest);
  }, [availableCharacters.data]);
  const defaultTab =
    selectedCharacterMeta && isCustomCharacter(selectedCharacterMeta)
      ? CUSTOM_TAB
      : DEFAULT_TAB;
  const [subPanelKey, setSubPanelKey] = useState('');
  const [activeTab, setActiveTab] = useState<CharacterTab>(defaultTab);
  const visibleCharacters =
    activeTab === CUSTOM_TAB ? customCharacters : freeCharacters;
  const isCharacterPickerOpen = subPanelKey === CHARACTER_PICKER_SUBPANEL_KEY;
  const isHandPickerOpen = subPanelKey === HAND_PICKER_SUBPANEL_KEY;
  const isFacePickerOpen = subPanelKey === FACE_PICKER_SUBPANEL_KEY;
  const showHandPicker = !isMhrCharacter;
  const showFacePicker = !isMhrCharacter && hasFace(selectedCharacterMeta);
  const showSkinTonePicker =
    !isMhrCharacter && hasSkinTone(selectedCharacterMeta);
  const showAccentColorPicker = isMhrCharacter;
  const isLoadingCharacters =
    availableCharacters.status === 'initial' ||
    availableCharacters.status === 'pending';
  const hasCharacterLoadError = availableCharacters.status === 'error';
  const canShowCharacterControls = availableCharacters.status === 'ready';
  const handImageName =
    staticHands === HAND_OPTIONS.DEFAULT ? 'rps_hand' : staticHands;
  const faceDisplayName = getFaceDisplayName(faceExpression);
  useEffect(() => {
    if (!isCharacterPickerOpen) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, isCharacterPickerOpen]);
  useEffect(() => {
    if (isMhrCharacter && (isHandPickerOpen || isFacePickerOpen)) {
      setSubPanelKey('');
    }
  }, [isFacePickerOpen, isHandPickerOpen, isMhrCharacter]);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <Text variant="studio/body/medium/sm">Configure character</Text>
        {isLoadingCharacters ? (
          <CharacterPanelStatus>Loading characters…</CharacterPanelStatus>
        ) : hasCharacterLoadError ? (
          <CharacterPanelStatus>
            {availableCharacters.error ?? 'Failed to load characters.'}
          </CharacterPanelStatus>
        ) : (
          <>
            <SubPanelButton
              isOpen={isCharacterPickerOpen}
              onClick={() => {
                const nextIsOpen = !isCharacterPickerOpen;
                setSubPanelKey(nextIsOpen ? CHARACTER_PICKER_SUBPANEL_KEY : '');
                if (nextIsOpen) {
                  setActiveTab(defaultTab);
                }
              }}
              ref={pickerButtonRef}
            >
              <div className="flex gap-4 lg:flex-col lg:gap-2">
                <div className="-my-2 -ml-2 h-[60px] w-[60px] flex-none lg:-mx-2 lg:-mt-2 lg:mb-0 lg:h-auto lg:w-[calc(100%+16px)]">
                  {selectedCharacterMeta?.heroThumbnailPath ? (
                    <Image
                      className="lg:aspect-314/138 h-full w-full rounded-lg object-cover"
                      crossOrigin="anonymous"
                      src={selectedCharacterMeta.heroThumbnailPath}
                      alt={selectedCharacterMeta.characterName}
                    />
                  ) : (
                    <div className="bg-secondary lg:aspect-314/138 h-full w-full rounded-lg" />
                  )}
                </div>
                <div className="-mb-1.5 flex min-w-0 flex-1 items-center justify-between gap-4">
                  <div className="max-w-full text-left">
                    <Text variant="studio/body/medium/sm" as="h2">
                      Avatar
                    </Text>
                    <Text
                      variant="studio/body/medium/sm"
                      as="div"
                      className="text-gray-45 truncate"
                    >
                      {characterDisplayName}
                    </Text>
                  </div>
                  <div className="flex items-center gap-1">
                    <ChevronRightIcon className="text-gray-45" />
                  </div>
                </div>
              </div>
            </SubPanelButton>
            {isCharacterPickerOpen && (
              <SubPanel
                id={CHARACTER_PICKER_SUBPANEL_KEY}
                elementToTrack={pickerButtonRef.current}
                subPanelKey={subPanelKey}
                closePanel={() => setSubPanelKey('')}
              >
                <div className="mb-4 flex gap-4">
                  <ToggleGroup
                    stretch
                    className="gap-4"
                    value={activeTab}
                    onValueChange={(value) =>
                      setActiveTab(value as CharacterTab)
                    }
                    items={[
                      {
                        label: 'Custom characters',
                        value: CUSTOM_TAB,
                        content: (
                          <span className="flex gap-1">
                            Custom
                            <span className="text-gray-45">
                              ({customCharacters.length})
                            </span>
                          </span>
                        ),
                      },
                      {
                        label: 'Cartwheel characters',
                        value: DEFAULT_TAB,
                        content: (
                          <span className="flex gap-1">
                            Cartwheel
                            <span className="text-gray-45">
                              ({freeCharacters.length})
                            </span>
                          </span>
                        ),
                      },
                    ]}
                  />
                </div>

                {visibleCharacters.length > 0 ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {visibleCharacters.map((character) => {
                      const isProcessing = isCharacterProcessing(character);
                      const isActive = sceneCharacterID === character.jobID;

                      return (
                        <AnimationPreview
                          key={character.jobID}
                          active={isActive}
                          loading={isProcessing}
                          mediaPath={character.heroThumbnailPath}
                          mediaType="image"
                          aspect="animation"
                          title={character.characterName}
                          subtitle={
                            isFreeCharacter(character)
                              ? undefined
                              : formatDistance(
                                  character.createdAt,
                                  new Date(),
                                  {
                                    includeSeconds: false,
                                    addSuffix: true,
                                  },
                                )
                          }
                          className={isProcessing ? 'opacity-70' : ''}
                          onClick={async () => {
                            if (isProcessing) {
                              noticesStore.info(
                                'This character is still processing. Please wait a few minutes and then refresh.',
                              );
                              return;
                            }

                            if (isActive) {
                              return;
                            }

                            const didSelect = await onSelectCharacter(
                              character.jobID,
                              selectedReferenceName,
                            );
                            if (didSelect === false) {
                              return;
                            }
                            setSubPanelKey('');
                          }}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <Text variant="studio/body/sm" className="text-secondary">
                    No characters available in this list yet.
                  </Text>
                )}
              </SubPanel>
            )}
          </>
        )}
      </section>

      {canShowCharacterControls && (
        <>
          {showHandPicker && (
            <section className="flex flex-col gap-3">
              <SubPanelButton
                isOpen={isHandPickerOpen}
                onClick={() => {
                  setSubPanelKey(
                    isHandPickerOpen ? '' : HAND_PICKER_SUBPANEL_KEY,
                  );
                }}
                ref={handButtonRef}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <div className="-my-2 -ml-2">
                      <img
                        className="aspect-square w-[60px] rounded-lg object-cover"
                        src={`${baseURL}/images/static_hands/${handImageName}.webp`}
                        alt={staticHands}
                      />
                    </div>
                    <div className="text-left">
                      <Text variant="studio/body/medium/sm" as="h2">
                        Hands
                      </Text>
                      <Text
                        variant="studio/body/medium/sm"
                        as="div"
                        className="text-gray-45"
                      >
                        {getHandDisplayName(staticHands)}
                      </Text>
                    </div>
                  </div>
                  <ChevronRightIcon className="text-gray-45" />
                </div>
              </SubPanelButton>
              {isHandPickerOpen && (
                <SubPanel
                  id={HAND_PICKER_SUBPANEL_KEY}
                  elementToTrack={handButtonRef.current}
                  subPanelKey={subPanelKey}
                  closePanel={() => setSubPanelKey('')}
                >
                  <div
                    className={`mb-4 flex justify-center rounded-lg border-transparent bg-[#ECF4FF] py-4 dark:bg-[#282d33] ${
                      staticHands === HAND_OPTIONS.DEFAULT
                        ? 'shadow-outline-blue-2'
                        : ''
                    }`}
                  >
                    <Checkbox
                      id="motion-editor-animated-hands"
                      label="Use animated hands"
                      color="settings"
                      variant="checkbox"
                      checked={staticHands === HAND_OPTIONS.DEFAULT}
                      setChecked={(checked) => {
                        onCharacterSettingsChange(
                          {
                            staticHands: checked
                              ? HAND_OPTIONS.DEFAULT
                              : HAND_OPTIONS.RELAXED,
                          },
                          selectedReferenceName,
                        );
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {HAND_OPTIONS_LIST.map((handOption) => (
                      <AnimationPreview
                        key={handOption}
                        active={staticHands === handOption}
                        mediaPath={`${baseURL}/images/static_hands/${handOption}.webp`}
                        mediaType="image"
                        onClick={() => {
                          onCharacterSettingsChange(
                            {
                              staticHands: handOption,
                            },
                            selectedReferenceName,
                          );
                        }}
                      />
                    ))}
                  </div>
                </SubPanel>
              )}
            </section>
          )}

          {showFacePicker && (
            <section className="flex flex-col gap-3">
              <SubPanelButton
                isOpen={isFacePickerOpen}
                onClick={() => {
                  setSubPanelKey(
                    isFacePickerOpen ? '' : FACE_PICKER_SUBPANEL_KEY,
                  );
                }}
                ref={faceButtonRef}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <div className="-my-2 -ml-2">
                      <img
                        className="aspect-square w-[60px] rounded-lg object-cover"
                        src={`${baseURL}/images/static_faces_icons/${faceExpression}.webp`}
                        alt={faceDisplayName}
                      />
                    </div>
                    <div className="text-left">
                      <Text variant="studio/body/medium/sm" as="h2">
                        Face
                      </Text>
                      <Text
                        variant="studio/body/medium/sm"
                        as="div"
                        className="text-gray-45"
                      >
                        {faceDisplayName}
                      </Text>
                    </div>
                  </div>
                  <ChevronRightIcon className="text-gray-45" />
                </div>
              </SubPanelButton>
              {isFacePickerOpen && (
                <SubPanel
                  id={FACE_PICKER_SUBPANEL_KEY}
                  elementToTrack={faceButtonRef.current}
                  subPanelKey={subPanelKey}
                  closePanel={() => setSubPanelKey('')}
                >
                  <div className="grid grid-cols-4 gap-2">
                    {FACE_EXPRESSIONS_LIST.map((face) => (
                      <AnimationPreview
                        key={face}
                        active={faceExpression === face}
                        mediaPath={`${baseURL}/images/static_faces_icons/${face}.webp`}
                        mediaType="image"
                        onClick={() => {
                          onCharacterSettingsChange(
                            {
                              faceExpression: face,
                            },
                            selectedReferenceName,
                          );
                        }}
                      />
                    ))}
                  </div>
                </SubPanel>
              )}
            </section>
          )}

          {showSkinTonePicker && (
            <section className="border-primary dark:bg-gray-90 flex flex-col gap-3 rounded-2xl border bg-white p-4">
              <Text variant="studio/body/medium/sm">Skin color</Text>
              <ColorPicker
                presets={[
                  SkinTone.LIGHTEST,
                  SkinTone.LIGHT,
                  SkinTone.MEDIUM,
                  SkinTone.DARK,
                  SkinTone.DARKEST,
                ]}
                value={defaultSkinTone(skinHex, charUploadID)}
                onChange={(value) => {
                  onCharacterSettingsChange(
                    {
                      skinHex: value,
                    },
                    selectedReferenceName,
                  );
                }}
              />
            </section>
          )}

          {showAccentColorPicker && (
            <section className="border-primary dark:bg-gray-90 flex flex-col gap-3 rounded-2xl border bg-white p-4">
              <Text variant="studio/body/medium/sm">Accent color</Text>
              <HuePicker
                value={defaultSkinTone(skinHex, charUploadID)}
                onChange={(value) => {
                  onCharacterSettingsChange(
                    {
                      skinHex: value,
                    },
                    selectedReferenceName,
                  );
                }}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}

function CharacterPanelStatus({ children }: { children: string }) {
  return (
    <div
      className="bg-secondary flex items-center gap-2 rounded-xl p-4"
      role="status"
    >
      <Text variant="studio/body/sm" className="text-secondary text-pretty">
        {children}
      </Text>
    </div>
  );
}

function findCharacterMeta(
  availableCharacters: Character[] | undefined,
  characterID?: string,
) {
  if (!availableCharacters?.length || !characterID) {
    return null;
  }

  const aliases = getCharacterIDAliases(characterID);
  return (
    availableCharacters.find((character) =>
      aliases.includes(character.jobID),
    ) ?? null
  );
}

function getCharacterIDAliases(characterID: string) {
  const baseCharacterID = characterID.replace(/_\d+$/, '');
  return Array.from(new Set([characterID, baseCharacterID]));
}

function formatSceneCharacterLabel(characterID?: string) {
  if (!characterID) {
    return 'No character selected';
  }

  if (characterID.startsWith('char-upload-')) {
    return characterID.slice('char-upload-'.length);
  }

  if (characterID.startsWith('character-upload-')) {
    return characterID.slice('character-upload-'.length);
  }

  return characterID;
}

function isFreeCharacter(character: Character) {
  return character.createdBy === 'FREE_CHARACTERS';
}

function isCustomCharacter(character: Character) {
  return character.createdBy !== 'FREE_CHARACTERS';
}

function isCharacterProcessing(character: Character) {
  if (
    character.createdBy.startsWith('service-') &&
    character.characterConfigURL
  ) {
    return false;
  }

  return [
    'PENDING',
    'AUTORIGGING_IN_PROGRESS',
    'RETARGETING_IN_PROGRESS',
  ].includes(character.uploadStatus);
}

function byLatest(a: Character, b: Character) {
  const dateA = new Date(a.createdAt);
  const dateB = new Date(b.createdAt);
  if (dateA > dateB) return -1;
  if (dateA < dateB) return 1;
  return 0;
}

function byName(a: Character, b: Character) {
  if (a.characterName < b.characterName) return -1;
  if (a.characterName > b.characterName) return 1;
  return 0;
}

function hasFace(character?: Character | null) {
  const isFree = character?.createdBy === 'FREE_CHARACTERS';
  const isAxel = character?.characterName === 'Axel';
  return isFree && !isAxel;
}

function hasSkinTone(character?: Character | null) {
  const isFree = character?.createdBy === 'FREE_CHARACTERS';
  const isAxel = character?.characterName === 'Axel';
  return isFree && !isAxel;
}

function getHandDisplayName(handName: string) {
  if (handName === HAND_OPTIONS.DEFAULT) {
    return 'Animated';
  }

  return handName.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function getFaceDisplayName(face: string) {
  if (face.includes('Look')) {
    return face.replace('Look', ' Look ');
  }

  if (face === 'DelightedGrin') {
    return 'Delighted Grin';
  }

  if (face === 'SadEyesClosed') {
    return 'Sad Eyes Closed';
  }

  return face
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\s+/, '')
    .replace(/^\w/, (c) => c.toUpperCase());
}
